import React from 'react';
import { calc, ptIfSet } from './calc.js';
import { Banner, Btn, Diamond, Field, OptCard, PtBadge, RadioList, Slider, TextArea, TextInput } from './components.jsx';
import { COMPANY_KINDS, PLATFORM_TYPES, PROJECT_KINDS } from './config.js';
import { t, tLabel, tLabelPair } from './i18n.js';
const { useState, useEffect, useRef } = React;

// ============================================================================
// PRE-STEP — Briefing
// ============================================================================
const PreStep = ({ state, setState, onSubmit, errors, requestAi }) => {
  const { pre } = state;
  const set = (patch) => setState(s => ({ ...s, pre: { ...s.pre, ...patch } }));

  const titleInvalid = errors?.title;
  const descInvalid  = errors?.description;
  const ptInvalid    = errors?.platformType;
  const pkInvalid    = errors?.projectKind;
  const ckInvalid    = errors?.companyKind;

  // RadioList expects {id, title, sub} — resolve titles/subs through i18n.
  const projectKindsLocalized = PROJECT_KINDS.map(pk => ({ id: pk.id, ...tLabelPair('project_kinds', pk) }));

  // Calc-Overlay: Marginal-PT pro Option (nur projectKind und companyKind wirken auf calc)
  const ptProjectKind = {}; PROJECT_KINDS.forEach(pk => { ptProjectKind[pk.id] = ptIfSet(state, 'pre.projectKind', pk.id); });
  const ptCompanyKind = {}; COMPANY_KINDS.forEach(ck => { ptCompanyKind[ck.id] = ptIfSet(state, 'pre.companyKind', ck.id); });

  return (
    <div style={{ maxWidth: 980, margin: '0 auto' }}>
      <header style={{ marginBottom: 24 }}>
        <span className="block__cat">{t('preStep.section_label')}</span>
        <h1 className="block__title" style={{ fontSize: 24, marginTop: 6 }}>{t('preStep.headline')}</h1>
      </header>

      {/* === Briefing === */}
        <div className="block is-card">
          <div className="block__head">
            <div>
              <span className="block__cat">{t('preStep.card_briefing_cat')}</span>
              <h2 className="block__title">{t('preStep.card_briefing_title')}</h2>
            </div>
          </div>
          <Field label={t('preStep.title_label')} required>
            <TextInput value={pre.title} onChange={(v) => set({ title: v })}
              invalid={titleInvalid}
              placeholder={t('preStep.title_placeholder')}/>
            {titleInvalid && <div className="field__error"><i className="fa-solid fa-circle-exclamation"></i>{t('errors.field_title_required')}</div>}
          </Field>

          <div style={{ paddingTop: 10 }}>
          <Field
            label={t('preStep.description_label')}
            required
          >
            <div className="mod-ai-banner" style={{ marginBottom: 10 }}>
              <i className="fa-solid fa-circle-info"></i>
              <span>{t('preStep.description_hint')}</span>
            </div>
            <TextArea
              value={pre.description}
              onChange={(v) => set({ description: v })}
              invalid={descInvalid}
              max={5000}
              showCount
              placeholder={t('preStep.description_placeholder')}
              autoGrow
              maxHeight={800}
              style={{ minHeight: 180, resize: 'none' }}
            />
            {descInvalid && <div className="field__error"><i className="fa-solid fa-circle-exclamation"></i>{t('errors.field_description_required')}</div>}
          </Field>
          </div>
        </div>

        {/* === Plattformart === */}
        <div className="block is-card">
          <div className="block__head">
            <div>
              <span className="block__cat">{t('preStep.card_platform_cat')}</span>
              <h2 className="block__title">{t('preStep.platform_label')}</h2>
            </div>
          </div>
          <div className="cardgrid" data-cols="4">
            {PLATFORM_TYPES.map(pt => (
              <OptCard
                key={pt.id}
                icon={pt.icon}
                title={tLabel('platform_types', pt)}
                radio
                selected={pre.platformType === pt.id}
                onClick={() => set({ platformType: pre.platformType === pt.id ? null : pt.id })}
              />
            ))}
          </div>
          {ptInvalid && <div className="field__error" style={{ marginTop: 8 }}><i className="fa-solid fa-circle-exclamation"></i>{t('errors.field_platform_required')}</div>}
        </div>

        {/* === Projektart === */}
        <div className="block is-card">
          <div className="block__head">
            <div>
              <span className="block__cat">{t('preStep.card_project_cat')}</span>
              <h2 className="block__title">{t('preStep.project_kind_label')}</h2>
            </div>
          </div>
          <RadioList
            options={projectKindsLocalized}
            value={pre.projectKind}
            onChange={(v) => set({ projectKind: v })}
            ptValues={ptProjectKind}
          />
          {pkInvalid && <div className="field__error" style={{ marginTop: 8 }}><i className="fa-solid fa-circle-exclamation"></i>{t('errors.field_project_kind_required')}</div>}
        </div>

        {/* === Unternehmen === */}
        <div className="block is-card">
          <div className="block__head">
            <div>
              <span className="block__cat">{t('preStep.card_company_cat')}</span>
              <h2 className="block__title">{t('preStep.company_kind_label')}</h2>
            </div>
          </div>
          <div className="cardgrid" data-cols="4">
            {COMPANY_KINDS.map(ck => {
              const lp = tLabelPair('company_kinds', ck);
              return (
                <OptCard
                  key={ck.id}
                  title={lp.title}
                  sub={lp.sub}
                  radio
                  selected={pre.companyKind === ck.id}
                  onClick={() => set({ companyKind: pre.companyKind === ck.id ? null : ck.id })}
                  badge={<PtBadge value={ptCompanyKind[ck.id]} dim={pre.companyKind !== ck.id} hideZero={false}/>}
                />
              );
            })}
          </div>
          {ckInvalid && <div className="field__error" style={{ marginTop: 8 }}><i className="fa-solid fa-circle-exclamation"></i>{t('errors.field_company_kind_required')}</div>}
        </div>

        {/* === Umsetzungszeitraum === */}
        <div className="block is-card">
          <div className="block__head">
            <div>
              <span className="block__cat">{t('preStep.card_timeline_cat')}</span>
              <h2 className="block__title">{t('preStep.timeline_label')}</h2>
            </div>
          </div>
          <Field>
            <Slider
              value={pre.timelineMonths ?? 6}
              onChange={(v) => set({ timelineMonths: v })}
              min={6} max={15} step={1}
              format={(v) => t(v === 1 ? 'preStep.timeline_month_one' : 'preStep.timeline_month_more', { value: v })}
            />
          </Field>
        </div>

        <Banner tone="next-steps" icon="wand-magic-sparkles" title={t('preStep.next_steps_title')}>
          {t('preStep.next_steps_body')}
        </Banner>

      <div className="between" style={{ paddingTop: 8, borderTop: '1px solid var(--border-color-soft)' }}>
        <Btn kind="ghost" onClick={() => onSubmit({ skipAi: true })} icon="forward">
          {t('actions.ki_skip')}
        </Btn>
        <Btn kind="link" icon="wand-magic-sparkles" onClick={requestAi} className="btn-violet">
          {t('actions.ki_prefill_long')}
        </Btn>
      </div>
    </div>
  );
};

// ============================================================================
// SYNAPSE CANVAS — violet neural-network background animation
// ============================================================================
const SynapseCanvas = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = 0, H = 0;
    let nodes = [];
    let zaps = [];
    let raf = 0;
    const rand = (a, b) => a + Math.random() * (b - a);
    const seedNodes = () => {
      const count = Math.round((W * H) / 26000);
      nodes = [];
      for (let i = 0; i < count; i++) {
        nodes.push({ x: Math.random() * W, y: Math.random() * H, vx: rand(-0.05, 0.05), vy: rand(-0.05, 0.05), r: rand(1, 2.2), pulse: Math.random() * Math.PI * 2 });
      }
    };
    const resize = () => {
      W = canvas.clientWidth; H = canvas.clientHeight;
      canvas.width = Math.floor(W * dpr); canvas.height = Math.floor(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedNodes();
    };
    const buildPath = (a, b) => {
      const pts = [{ x: a.x, y: a.y }];
      const dx = b.x - a.x, dy = b.y - a.y;
      const dist = Math.hypot(dx, dy);
      const segs = Math.max(4, Math.round(dist / 36));
      const nx = -dy / dist, ny = dx / dist;
      for (let i = 1; i < segs; i++) {
        const t = i / segs;
        const jitter = (Math.random() - 0.5) * Math.min(28, dist * 0.18);
        pts.push({ x: a.x + dx * t + nx * jitter, y: a.y + dy * t + ny * jitter });
      }
      pts.push({ x: b.x, y: b.y });
      const branches = [];
      if (Math.random() < 0.55 && pts.length > 3) {
        const i = 1 + Math.floor(Math.random() * (pts.length - 2));
        const base = pts[i];
        const len = rand(18, 44);
        const ang = Math.atan2(dy, dx) + rand(-1.2, 1.2);
        const bpts = [base];
        for (let k = 1; k <= 3; k++) {
          const t = k / 3;
          bpts.push({ x: base.x + Math.cos(ang) * len * t + (Math.random() - 0.5) * 8, y: base.y + Math.sin(ang) * len * t + (Math.random() - 0.5) * 8 });
        }
        branches.push(bpts);
      }
      return { pts, branches };
    };
    const spawnZap = () => {
      if (nodes.length < 2) return;
      const a = nodes[Math.floor(Math.random() * nodes.length)];
      let b = null, bestD = Infinity;
      for (let i = 0; i < 6; i++) {
        const c = nodes[Math.floor(Math.random() * nodes.length)];
        if (c === a) continue;
        const d = Math.hypot(c.x - a.x, c.y - a.y);
        if (d > 80 && d < 360 && d < bestD) { bestD = d; b = c; }
      }
      if (!b) return;
      const path = buildPath(a, b);
      zaps.push({ ...path, born: performance.now(), life: rand(450, 900), hue: rand(265, 285) });
    };
    const drawPath = (pts, alpha, hue, width = 1.4) => {
      ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      ctx.strokeStyle = `hsla(${hue}, 95%, 70%, ${alpha * 0.18})`;
      ctx.lineWidth = width + 6;
      ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
      ctx.stroke();
      ctx.strokeStyle = `hsla(${hue}, 100%, 88%, ${alpha})`;
      ctx.lineWidth = width;
      ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
      ctx.stroke();
    };
    let lastSpawn = 0;
    const tick = (now) => {
      ctx.clearRect(0, 0, W, H);
      for (const n of nodes) {
        n.x += n.vx; n.y += n.vy;
        if (n.x < -10) n.x = W + 10; if (n.x > W + 10) n.x = -10;
        if (n.y < -10) n.y = H + 10; if (n.y > H + 10) n.y = -10;
        n.pulse += 0.02;
        const p = 0.5 + 0.5 * Math.sin(n.pulse);
        const grd = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, 10);
        grd.addColorStop(0, `hsla(275, 90%, 75%, ${0.35 + p * 0.25})`);
        grd.addColorStop(1, 'hsla(275, 90%, 75%, 0)');
        ctx.fillStyle = grd;
        ctx.beginPath(); ctx.arc(n.x, n.y, 10, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = `hsla(280, 100%, 92%, ${0.55 + p * 0.35})`;
        ctx.beginPath(); ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2); ctx.fill();
      }
      if (now - lastSpawn > rand(35, 95)) {
        spawnZap();
        if (Math.random() < 0.6) spawnZap();
        if (Math.random() < 0.3) spawnZap();
        lastSpawn = now;
      }
      zaps = zaps.filter(z => {
        const t = (now - z.born) / z.life;
        if (t >= 1) return false;
        const a = t < 0.12 ? (t / 0.12) : (1 - (t - 0.12) / (1 - 0.12));
        const alpha = Math.max(0, a);
        drawPath(z.pts, alpha, z.hue, 1.6);
        for (const br of z.branches) drawPath(br, alpha * 0.7, z.hue, 1.1);
        return true;
      });
      raf = requestAnimationFrame(tick);
    };
    resize();
    window.addEventListener('resize', resize);
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={canvasRef} className="loading__synapses" aria-hidden="true" />;
};

// ============================================================================
// LOADING OVERLAY
// ============================================================================
const LoadingOverlay = ({ open, currentStep = 0 }) => {
  if (!open) return null;
  const steps = [
    t('loading_overlay.step_0'),
    t('loading_overlay.step_1'),
    t('loading_overlay.step_2'),
    t('loading_overlay.step_3'),
  ];
  return (
    <div className="loading loading--violet">
      <SynapseCanvas/>
      <div className="loading__card" role="dialog" aria-modal="true" aria-label={t('loading_overlay.title')}>
        <div className="loading__diamond"><Diamond/></div>
        <div className="loading__title">{t('loading_overlay.title')}</div>
        <div className="loading__body">{t('loading_overlay.body')}</div>
        <div className="loading__progress" aria-hidden="true">
          <span key={open ? 'on' : 'off'} className="loading__progress-fill"></span>
        </div>
        <div className="loading__steps">
          {steps.map((s, i) => {
            const cls = i < currentStep ? 'is-done' : i === currentStep ? 'is-active' : '';
            return (
              <div key={i} className={`loading__step ${cls}`}>
                <span className="ix">
                  {i < currentStep && <i className="fa-solid fa-check" style={{ fontSize: 9 }}></i>}
                  {i === currentStep && <span className="spinner"></span>}
                  {i > currentStep && <span style={{ fontSize: 9 }}>{i + 1}</span>}
                </span>
                <span>{s}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export { PreStep, LoadingOverlay };
